/**
 * MITT JOBB — startsidan alla ser, oavsett roll. Prototypens data-screen="hem".
 *
 * FYRA KAKEL OCH EN KALENDER, ingenting mer. Kunden är den arbetande chefen med
 * handskar på i ett trapphus: fyra stora ytor att träffa, och längst ner
 * månaden som prickar så att han på en sekund ser vilka dagar han lagt in tid
 * på. Trycker han på kalendern öppnas den STORA månadskalendern (skärmen
 * 'kalender') — aldrig en lista med ordrar.
 *
 * TALEN ÄR RIKTIGA OCH KOMMER UR SYNC_PULL. Se skarmar/oversikt.ts: en pull,
 * vikt ihop till minuter per dag, öppna ordrar, mil och utlägg. Står det ett
 * tal i ett kakel är det hämtat; är det inte hämtat står det ingenting.
 *
 * TVÅ REGLER OM SIFFRORNA:
 *   BESLUT 04. Varje summa är per ISO-VECKA. Prototypen skrev "13,5 h i sep",
 *   men månaden finns inte i tidrapporteringen — attest och lås hänger på
 *   veckan, och ett tal per månad hade lärt chefen fel periodbegrepp.
 *   Månadsrutnätet under är NAVIGERING och får därför ingen månadstotal; dess
 *   räkning är ANTAL DAGAR med tid, vilket beskriver rutorna man ser.
 *
 *   NOTISSIFFRAN ÄR VAD SOM VÄNTAR PÅ DIG, aldrig en total. På Arbetsordrar är
 *   det de öppna ordrar du står som ansvarig för, på Tidrapport de vardagar i
 *   veckan som gått utan både tid och frånvaro.
 *
 * DAG ETT ÄR EN EGEN SKÄRM, INTE FYRA TOMMA KAKEL. Så länge firman saknar något
 * av de tre som krävs för att spara en timme — ett timpris för din roll, minst
 * ett moment, en arbetsorder — står ett kort HÖGST UPP som säger vad som saknas
 * och leder till skärmen 'igang'. Under den tiden är kaklen garanterat tomma,
 * och ett kakel som säger "Inga öppna just nu" till någon som aldrig haft ett
 * jobb beskriver ingenting: texterna byts då till vad man ska göra. Listan
 * kommer ur kravenFor() i oversikt.ts, samma källa som kom-igång-skärmen räknar
 * ur — kortet och skärmen kan inte säga olika saker.
 */

import {
  Felruta,
  Kakel,
  Kakelrutnat,
  Knapp,
  Laddar,
  Lagesvaxel,
  Litetmanadsrutnat,
  Datumrad,
  Tomtext,
  Yta,
} from '../ui';
import { idag, veckaFor, visaDag, visaMil, visaMinuter, visaOren } from '../app/datum';
import { useInloggad } from '../app/session';
import { useNavigering } from '../app/navigering';
import {
  ROLLNAMN,
  dagarMedTid,
  dagarSomSaknas,
  kravenFor,
  laddaOmOversikt,
  manadFor,
  meterIVecka,
  minuterIVecka,
  rutnatFor,
  useOversikt,
  utlaggIVecka,
  visaManad,
} from './oversikt';
import type { Oversikt } from './oversikt';
import { Prenumerationsbanner } from './Prenumerationsbanner';

const dagord = (antal: number): string => (antal === 1 ? '1 dag saknas' : `${antal} dagar saknas`);

/* --- Undertexterna. Utan hämtad översikt står det ingenting. ------------- */

const ordertext = (o: Oversikt | null, dagEtt: boolean): string | undefined => {
  if (o === null) return undefined;
  // EN FIRMA SOM ALDRIG HAFT ETT JOBB HAR INGA "öppna just nu" — den har inte
  // börjat. Texten ska säga vad man gör, inte beskriva ett tomrum.
  if (o.oppnaOrdrar === 0) return dagEtt ? 'Lägg upp ditt första jobb' : 'Inga öppna just nu';
  return o.minaOppnaOrdrar > 0
    ? `${o.oppnaOrdrar} öppna · ${o.minaOppnaOrdrar} på dig`
    : `${o.oppnaOrdrar} öppna`;
};

export const Hem = () => {
  const { konto, aktiv, roll, arChef, firmaId } = useInloggad();
  const { ga } = useNavigering();
  const lage = useOversikt();

  const nu = idag();
  const vecka = veckaFor(nu);
  const manad = manadFor(nu);
  const o = lage.slag === 'klar' ? lage.oversikt : null;

  const minuter = o === null ? 0 : minuterIVecka(o, vecka);
  const saknas = o === null ? [] : dagarSomSaknas(o, vecka, nu);
  const meter = o === null ? 0 : meterIVecka(o, vecka);
  const utlagg = o === null ? { antal: 0, oren: 0 } : utlaggIVecka(o, vecka);
  const rutnat = rutnatFor(o, manad, nu, false);
  const meddag = dagarMedTid(o, manad);

  /*
    VAD SOM SAKNAS INNAN EN TIMME GÅR ATT SPARA. Tom lista i tre helt olika
    lägen som alla ska se likadana ut: allt finns, vyn bar ingen prisdata
    (montör och gäst — beslut 16), eller den som tittar inte får skriva stamdata
    ändå. `arChef` är samma spärr som skärmen 'igang' har, så kortet kan inte
    leda någon till ett besked om att det inte var hans uppgift.
  */
  const krav = kravenFor(o, arChef);
  const dagEtt = krav.length > 0;

  return (
    <>
      {/* VÄXELN DÖLJS MEDAN FIRMAN INTE ÄR IGÅNG. Laget-läget kan då inte
          innehålla någonting — se Lagesvaxel.tsx. */}
      <Lagesvaxel dolj={dagEtt} />
      {/* Namnet står inte här längre: initialerna i toppraden säger vem som är
          inloggad, och raden bröt på två rader så fort firman hette något
          längre än "Ek Bygg". */}
      <Datumrad stark={visaDag(nu)}>
        {ROLLNAMN[roll]} hos {aktiv.name}
      </Datumrad>

      {/* PROVPERIOD, OBETALD FAKTURA ELLER PAUSAD FIRMA. Bannern visar sig själv
          bara när något av det gäller OCH bara för chefen — se
          Prenumerationsbanner.tsx. Den står ovanför kaklen med flit: en firma som
          är på väg att låsas ska inte behöva leta i inställningarna. */}
      <Prenumerationsbanner />

      {/*
        KOM IGÅNG — kortet som ersatte ett kugghjul ingenting pekade mot.

        DET ÄR MEDVETET KORT: en rubrik, en rad, en knapp. Tre listrader med
        varje steg hade varit mer information och sämre gränssnitt — den som
        just lagt upp sin firma ska se EN sak att trycka på, inte en checklista
        att läsa och rangordna. Vad stegen är står på nästa skärm, ett i taget,
        och samma kravenFor() räknar dem där.

        DEN STÅR ÖVER KAKLEN av samma skäl som prenumerationsbannern: det som
        blockerar hela appen ska inte ligga under det som är blockerat.
      */}
      {dagEtt && (
        <Yta
          rubrik="Innan du kan rapportera"
          rakning={krav.length === 1 ? '1 steg' : `${String(krav.length)} steg`}
        >
          <Tomtext>
            {/* FÖRSTA ORDET BEHÅLLER SIN VERSAL. Alla tre rubrikerna är hela
                meningar var för sig ("Sätt ditt timpris"), och gemena dem alla
                gav en rad som ser avhuggen ut: "sätt ditt timpris, välj ...". */}
            {krav
              .map((k, i) => (i === 0 ? k.rubrik : k.rubrik.toLowerCase()))
              .join(', ')}
            . Det tar en minut.
          </Tomtext>
          <Knapp onClick={() => ga('igang')}>Kom igång</Knapp>
        </Yta>
      )}

      {lage.slag === 'fel' && (
        <Felruta
          fel={lage.fel}
          atgard={
            <Knapp
              variant="piller-mini"
              onClick={() => laddaOmOversikt(firmaId, konto.id)}
            >
              Försök igen
            </Knapp>
          }
        />
      )}

      <Kakelrutnat>
        <Kakel
          ikon="ordrar"
          titel="Arbetsordrar"
          text={ordertext(o, dagEtt)}
          notis={o?.minaOppnaOrdrar}
          till="ordrar"
        />
        <Kakel
          ikon="tid"
          titel="Tidrapport"
          text={
            o === null
              ? undefined
              : saknas.length > 0
                ? `${visaMinuter(minuter)} i veckan · ${dagord(saknas.length)}`
                : `${visaMinuter(minuter)} i veckan`
          }
          notis={saknas.length}
          till="tid"
        />
        <Kakel
          ikon="mil"
          titel="Mil"
          text={o === null ? undefined : `${visaMil(meter)} i veckan`}
          till="mil"
        />
        <Kakel
          ikon="utlagg"
          titel="Utlägg"
          text={
            o === null
              ? undefined
              : utlagg.antal === 0
                ? 'Inga i veckan'
                : `${utlagg.antal} st · ${visaOren(utlagg.oren)} ex moms`
          }
          till="utlagg"
        />
      </Kakelrutnat>

      {/*
        KALENDERN I UNDERKANTEN. Prickarna sitter på de dagar som har tid på
        sig; en tryckning öppnar den stora månadskalendern på just den dagen.
        Månaden är en NAVIGERINGSETIKETT, inte en period — därför "13 dagar med
        tid" och inte en timsumma. Beslut 04.
      */}
      <Yta
        rubrik={visaManad(manad)}
        rakning={
          lage.slag === 'laddar'
            ? 'hämtar'
            : meddag === 1
              ? '1 dag med tid'
              : `${meddag} dagar med tid`
        }
      >
        {lage.slag === 'laddar' ? (
          <Laddar rader={2} />
        ) : (
          <Litetmanadsrutnat
            dagar={rutnat.dagar}
            tomma={rutnat.tomma}
            onValj={(dag) => ga('kalender', { dag })}
          />
        )}
      </Yta>

      {/* En rad, inte ett stycke: startsidan ska rymma kaklen och kalendern
          utan rullning på en iPhone 14, och hjälptexten är det som får vika. */}
      <Tomtext>Skriv in tiden när jobbet är gjort — inget stoppur.</Tomtext>
    </>
  );
};
